import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ReceivingChart } from 'yankees-react-utils';

const StyledChartContainer = styled.div`
    display: flex;
    justify-content: space-evenly;
    flex-wrap: wrap;
`;

const getPercent = (numerator, denominator) => {
    if (!denominator) return '--';
    return 100 * ((numerator / denominator) - 1);
};

const getGainedLostData = (translate, totalData) => {
    const overallData = totalData.find((d) => d.ready === null && d.relaxed === null && d.fb === null && d.runners_on === null && d.bases_empty === null);
    const robData = totalData.find((d) => d.runners_on === true);
    const emptyData = totalData.find((d) => d.bases_empty === true);
    const fbData = totalData.find((d) => d.fb === true);
    const fb95Data = totalData.find((d) => d.fb95 === true);
    const nonfbData = totalData.find((d) => d.fb === false);

    const overall = overallData && {
        split: translate('sections.reports.catcher.receiving.overall'),
        up: getPercent(overallData.strikes_up, overallData.pstrikes_up),
        down: getPercent(overallData.strikes_down, overallData.pstrikes_down),
        gloveSide: getPercent((overallData.strikes_rhh_in + overallData.strikes_lhh_away), (overallData.pstrikes_rhh_in + overallData.pstrikes_lhh_away)),
        armSide: getPercent((overallData.strikes_rhh_away + overallData.strikes_lhh_in), (overallData.pstrikes_rhh_away + overallData.pstrikes_lhh_in)),
        total: getPercent((overallData.strikes_up + overallData.strikes_down + overallData.strikes_rhh_in + overallData.strikes_rhh_away + overallData.strikes_lhh_away + overallData.strikes_lhh_in), (overallData.pstrikes_up + overallData.pstrikes_down + overallData.pstrikes_rhh_in + overallData.pstrikes_rhh_away + overallData.pstrikes_lhh_away + overallData.pstrikes_lhh_in)),
    };
    const h2h = overallData && {
        split: translate('sections.reports.catcher.receiving.h2h'),
        up: getPercent(overallData.opp_strikes_up, overallData.opp_pstrikes_up),
        down: getPercent(overallData.opp_strikes_down, overallData.opp_pstrikes_down),
        gloveSide: getPercent((overallData.opp_strikes_rhh_in + overallData.opp_strikes_lhh_away), (overallData.opp_pstrikes_rhh_in + overallData.opp_pstrikes_lhh_away)),
        armSide: getPercent((overallData.opp_strikes_rhh_away + overallData.opp_strikes_lhh_in), (overallData.opp_pstrikes_rhh_away + overallData.opp_pstrikes_lhh_in)),
        total: getPercent((overallData.opp_strikes_up + overallData.opp_strikes_down + overallData.opp_strikes_rhh_in + overallData.opp_strikes_rhh_away + overallData.opp_strikes_lhh_away + overallData.opp_strikes_lhh_in), (overallData.opp_pstrikes_up + overallData.opp_pstrikes_down + overallData.opp_pstrikes_rhh_in + overallData.opp_pstrikes_rhh_away + overallData.opp_pstrikes_lhh_away + overallData.opp_pstrikes_lhh_in)),
    };
    const ready = robData && {
        split: translate('sections.reports.catcher.receiving.abbr-run-on-base'),
        up: getPercent(robData.strikes_up, robData.pstrikes_up),
        down: getPercent(robData.strikes_down, robData.pstrikes_down),
        gloveSide: getPercent((robData.strikes_rhh_in + robData.strikes_lhh_away), (robData.pstrikes_rhh_in + robData.pstrikes_lhh_away)),
        armSide: getPercent((robData.strikes_rhh_away + robData.strikes_lhh_in), (robData.pstrikes_rhh_away + robData.pstrikes_lhh_in)),
        total: getPercent((robData.strikes_up + robData.strikes_down + robData.strikes_rhh_in + robData.strikes_rhh_away + robData.strikes_lhh_away + robData.strikes_lhh_in), (robData.pstrikes_up + robData.pstrikes_down + robData.pstrikes_rhh_in + robData.pstrikes_rhh_away + robData.pstrikes_lhh_away + robData.pstrikes_lhh_in)),
    };
    const relaxed = emptyData && {
        split: translate('sections.reports.catcher.receiving.empty'),
        up: getPercent(emptyData.strikes_up, emptyData.pstrikes_up),
        down: getPercent(emptyData.strikes_down, emptyData.pstrikes_down),
        gloveSide: getPercent((emptyData.strikes_rhh_in + emptyData.strikes_lhh_away), (emptyData.pstrikes_rhh_in + emptyData.pstrikes_lhh_away)),
        armSide: getPercent((emptyData.strikes_rhh_away + emptyData.strikes_lhh_in), (emptyData.pstrikes_rhh_away + emptyData.pstrikes_lhh_in)),
        total: getPercent((emptyData.strikes_up + emptyData.strikes_down + emptyData.strikes_rhh_in + emptyData.strikes_rhh_away + emptyData.strikes_lhh_away + emptyData.strikes_lhh_in), (emptyData.pstrikes_up + emptyData.pstrikes_down + emptyData.pstrikes_rhh_in + emptyData.pstrikes_rhh_away + emptyData.pstrikes_lhh_away + emptyData.pstrikes_lhh_in)),
    };
    const fb = fbData && {
        split: translate('baseball.pitches.abbr-fastball'),
        up: getPercent(fbData.strikes_up, fbData.pstrikes_up),
        down: getPercent(fbData.strikes_down, fbData.pstrikes_down),
        gloveSide: getPercent((fbData.strikes_rhh_in + fbData.strikes_lhh_away), (fbData.pstrikes_rhh_in + fbData.pstrikes_lhh_away)),
        armSide: getPercent((fbData.strikes_rhh_away + fbData.strikes_lhh_in), (fbData.pstrikes_rhh_away + fbData.pstrikes_lhh_in)),
        total: getPercent((fbData.strikes_up + fbData.strikes_down + fbData.strikes_rhh_in + fbData.strikes_rhh_away + fbData.strikes_lhh_away + fbData.strikes_lhh_in), (fbData.pstrikes_up + fbData.pstrikes_down + fbData.pstrikes_rhh_in + fbData.pstrikes_rhh_away + fbData.pstrikes_lhh_away + fbData.pstrikes_lhh_in)),
    };
    const fb95 = fb95Data && {
        split: `95+ ${translate('baseball.pitches.abbr-fastball')}`,
        up: getPercent(fb95Data.strikes_up, fb95Data.pstrikes_up),
        down: getPercent(fb95Data.strikes_down, fb95Data.pstrikes_down),
        gloveSide: getPercent((fb95Data.strikes_rhh_in + fb95Data.strikes_lhh_away), (fb95Data.pstrikes_rhh_in + fb95Data.pstrikes_lhh_away)),
        armSide: getPercent((fb95Data.strikes_rhh_away + fb95Data.strikes_lhh_in), (fb95Data.pstrikes_rhh_away + fb95Data.pstrikes_lhh_in)),
        total: getPercent((fb95Data.strikes_up + fb95Data.strikes_down + fb95Data.strikes_rhh_in + fb95Data.strikes_rhh_away + fb95Data.strikes_lhh_away + fb95Data.strikes_lhh_in), (fb95Data.pstrikes_up + fb95Data.pstrikes_down + fb95Data.pstrikes_rhh_in + fb95Data.pstrikes_rhh_away + fb95Data.pstrikes_lhh_away + fb95Data.pstrikes_lhh_in)),
    };
    const nonfb = nonfbData && {
        split: translate('baseball.pitches.abbr-fastball-non'),
        up: getPercent(nonfbData.strikes_up, nonfbData.pstrikes_up),
        down: getPercent(nonfbData.strikes_down, nonfbData.pstrikes_down),
        gloveSide: getPercent((nonfbData.strikes_rhh_in + nonfbData.strikes_lhh_away), (nonfbData.pstrikes_rhh_in + nonfbData.pstrikes_lhh_away)),
        armSide: getPercent((nonfbData.strikes_rhh_away + nonfbData.strikes_lhh_in), (nonfbData.pstrikes_rhh_away + nonfbData.pstrikes_lhh_in)),
        total: getPercent((nonfbData.strikes_up + nonfbData.strikes_down + nonfbData.strikes_rhh_in + nonfbData.strikes_rhh_away + nonfbData.strikes_lhh_away + nonfbData.strikes_lhh_in), (nonfbData.pstrikes_up + nonfbData.pstrikes_down + nonfbData.pstrikes_rhh_in + nonfbData.pstrikes_rhh_away + nonfbData.pstrikes_lhh_away + nonfbData.pstrikes_lhh_in)),
    };
    return [overall, h2h, fb, fb95, nonfb, ready, relaxed];
};

const ReceivingGainedLost = ({ recData }) => {
    const { t: translate } = useTranslation();
    const { total } = recData;
    const chartData = getGainedLostData(translate, total);
    return (
        <StyledChartContainer>
            {chartData.map((c) => c && (
                <ReceivingChart
                    key={c.split}
                    split={c.split}
                    up={c.up}
                    down={c.down}
                    gloveSide={c.gloveSide}
                    armSide={c.armSide}
                    total={c.total}
                />
            ))}
        </StyledChartContainer>
    );
};

ReceivingGainedLost.propTypes = {
    recData: PropTypes.shape({
        total: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
};

export default ReceivingGainedLost;
